import React, { useEffect, useState } from 'react';
import { cqAPI } from '../../lib/Auth';
import {
    Text,
    Center,
    Button,
    VStack,
    Box,
    Flex,
    HStack,
    useTheme,
    useToast,
    Spacer,
    Badge,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    FormControl,
    FormLabel,
    Input,
    Select,
    useDisclosure,
} from '@chakra-ui/react';

function GDMSSettings() {
    const [isConnected, setIsConnected] = useState(false);
    const [status, setStatus] = useState(null);
    const [sites, setSites] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingSites, setIsLoadingSites] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [credentials, setCredentials] = useState({
        username: '',
        password: '',
        clientId: '',
        clientSecret: '',
        region: 'us'
    });
    const theme = useTheme();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const getStatus = async () => {
        try {
            setIsLoading(true);
            const response = await cqAPI.get('/settings/gdms/status');
            if (response.type === 'ok') {
                setStatus(response.data);
                setIsConnected(response.data.connected);
            } else if (response.type === 'error') {
                toast({
                    title: "Error",
                    description: response.msg,
                    status: "error",
                    duration: 7000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching GDMS status:', error);
            toast({
                title: "Error",
                description: "Failed to fetch GDMS status",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getSites = async () => {
        try {
            setIsLoadingSites(true);
            const response = await cqAPI.get('/settings/gdms/sites');
            if (response.type === 'ok') {
                setSites(response.data?.sites || response.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching GDMS sites:', error);
        } finally {
            setIsLoadingSites(false);
        }
    };

    useEffect(() => {
        getStatus();
    }, []);

    useEffect(() => {
        if (isConnected) {
            getSites();
        }
    }, [isConnected]);

    const handleConnect = async () => {
        try {
            setIsConnecting(true);
            const response = await cqAPI.post('/settings/gdms/authenticate', credentials);

            if (response.type === 'ok') {
                toast({
                    title: "GDMS Connected",
                    description: response.msg || "Successfully connected to GDMS",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                onClose(); // Close modal
                setCredentials({ username: '', password: '', clientId: '', clientSecret: '', region: 'us' }); // Clear form
                await getStatus();
            } else if (response.type === 'error') {
                toast({
                    title: "Connection Failed",
                    description: response.msg,
                    status: "error",
                    duration: 7000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error connecting to GDMS:', error);
            toast({
                title: "Error",
                description: "Failed to connect to GDMS",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            setIsDisconnecting(true);
            const response = await cqAPI.post('/settings/gdms/disconnect');

            if (response.type === 'ok') {
                toast({
                    title: "GDMS Disconnected",
                    description: response.msg || "Successfully disconnected from GDMS",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                await getStatus();
            } else if (response.type === 'error') {
                toast({
                    title: "Error",
                    description: response.msg,
                    status: "error",
                    duration: 7000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error disconnecting from GDMS:', error);
            toast({
                title: "Error",
                description: "Failed to disconnect from GDMS",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsDisconnecting(false);
        }
    };

    const handleTestConnection = async () => {
        try {
            setIsTesting(true);
            const response = await cqAPI.get('/settings/gdms/test');

            if (response.type === 'ok') {
                toast({
                    title: "Connection Test Successful",
                    description: response.msg || "GDMS connection is working",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
            } else if (response.type === 'error') {
                toast({
                    title: "Connection Test Failed",
                    description: response.msg,
                    status: "error",
                    duration: 7000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error testing GDMS connection:', error);
            toast({
                title: "Error",
                description: "Failed to test GDMS connection",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsTesting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const getStatusBadge = () => {
        if (!status) return null;

        const statusColors = {
            active: 'green',
            expired: 'orange',
            error: 'red'
        };

        return (
            <Badge colorScheme={statusColors[status.status] || 'gray'} fontSize="md" px={3} py={1}>
                {status.status?.toUpperCase()}
            </Badge>
        );
    };

    if (isLoading) {
        return (
            <Center height="100vh" width="100%">
                <Spinner size="xl" />
            </Center>
        );
    }

    return (
        <>
            {isConnected ? (
                <Box p={4} overflowY="auto" height="100%" width="100%">
                    <HStack mb={6}>
                        <Flex justifyContent="flex-start">
                            <Text className="page-header">GDMS Integration</Text>
                        </Flex>
                        <Spacer />
                        <Flex justifyContent="flex-end" direction="column" alignItems="flex-end">
                            <HStack spacing={3}>
                                <Button
                                    onClick={handleTestConnection}
                                    isLoading={isTesting}
                                    size="sm"
                                    variant="outline"
                                >
                                    Test Connection
                                </Button>
                                <Button
                                    onClick={handleDisconnect}
                                    isLoading={isDisconnecting}
                                    colorScheme="red"
                                    size="sm"
                                >
                                    Disconnect
                                </Button>
                            </HStack>
                        </Flex>
                    </HStack>

                    {/* Status Card */}
                    <Box
                        borderWidth="1px"
                        borderRadius="lg"
                        p={6}
                        mb={6}
                        bg={theme.colors.lightBackground}
                    >
                        <VStack align="stretch" spacing={4}>
                            <HStack>
                                <Text fontWeight="bold" fontSize="lg">Connection Status:</Text>
                                {getStatusBadge()}
                            </HStack>

                            {status?.organization && (
                                <Box>
                                    <Text fontWeight="semibold">Organization:</Text>
                                    <Text color={theme.colors.lightText}>{status.organization.name}</Text>
                                </Box>
                            )}

                            <Box>
                                <Text fontWeight="semibold">Region:</Text>
                                <Text color={theme.colors.lightText}>{status?.region?.toUpperCase() || 'US'}</Text>
                            </Box>

                            {status?.expiresAt && (
                                <Box>
                                    <Text fontWeight="semibold">Token Expires:</Text>
                                    <Text color={theme.colors.lightText}>{formatDate(status.expiresAt)}</Text>
                                </Box>
                            )}

                            {status?.lastVerified && (
                                <Box>
                                    <Text fontWeight="semibold">Last Verified:</Text>
                                    <Text color={theme.colors.lightText}>{formatDate(status.lastVerified)}</Text>
                                </Box>
                            )}
                        </VStack>
                    </Box>

                    {/* Error Alert - only show if status is not active */}
                    {status?.lastError?.message && status?.status !== 'active' && (
                        <Alert status="error" borderRadius="md" mb={6}>
                            <AlertIcon />
                            <Box flex="1">
                                <AlertTitle>Last Error</AlertTitle>
                                <AlertDescription>
                                    {status.lastError.message}
                                    {status.lastError.timestamp && (
                                        <Text fontSize="sm" mt={1}>
                                            {formatDate(status.lastError.timestamp)}
                                        </Text>
                                    )}
                                </AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Sites List */}
                    <Box
                        borderWidth="1px"
                        borderRadius="lg"
                        p={6}
                        mb={6}
                        bg={theme.colors.lightBackground}
                    >
                        <HStack mb={4}>
                            <Text fontWeight="bold" fontSize="lg">GDMS Sites</Text>
                            <Spacer />
                            {isLoadingSites && <Spinner size="sm" />}
                        </HStack>

                        {sites.length > 0 ? (
                            <Box overflowX="auto">
                                <Box
                                    as="table"
                                    width="100%"
                                    fontSize="sm"
                                    borderWidth="1px"
                                    borderRadius="md"
                                    overflow="hidden"
                                >
                                    <Box as="thead" bg={theme.colors.gray[100]}>
                                        <Box as="tr">
                                            <Box as="th" p={3} textAlign="left" fontWeight="semibold">Name</Box>
                                            <Box as="th" p={3} textAlign="left" fontWeight="semibold">Location</Box>
                                            <Box as="th" p={3} textAlign="left" fontWeight="semibold">Timezone</Box>
                                            <Box as="th" p={3} textAlign="left" fontWeight="semibold">Site ID</Box>
                                        </Box>
                                    </Box>
                                    <Box as="tbody">
                                        {sites.map((site, index) => (
                                            <Box
                                                as="tr"
                                                key={site.id || site.site_id || index}
                                                borderTopWidth={index > 0 ? "1px" : "0"}
                                                _hover={{ bg: theme.colors.gray[50] }}
                                            >
                                                <Box as="td" p={3}>{site.name}</Box>
                                                <Box as="td" p={3}>
                                                    {[site.city, site.state, site.country]
                                                        .filter(Boolean)
                                                        .join(', ') || '-'}
                                                </Box>
                                                <Box as="td" p={3}>{site.timezone || '-'}</Box>
                                                <Box as="td" p={3}>
                                                    <Text fontSize="xs" fontFamily="mono" color={theme.colors.gray[600]}>
                                                        {site.id || site.site_id}
                                                    </Text>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Text color={theme.colors.lightText} fontSize="sm">
                                {isLoadingSites ? 'Loading sites...' : 'No sites found'}
                            </Text>
                        )}
                    </Box>

                    {/* Info Box */}
                    <Box
                        borderWidth="1px"
                        borderRadius="lg"
                        p={6}
                        bg={theme.colors.gray[50]}
                    >
                        <VStack align="stretch" spacing={3}>
                            <Text fontWeight="bold" fontSize="md">About GDMS Integration</Text>
                            <Text fontSize="sm" color={theme.colors.lightText}>
                                GDMS (Grandstream Device Management System) integration allows you to automatically
                                create sites in GDMS when new businesses are created in Viva.
                            </Text>
                            <Text fontSize="sm" color={theme.colors.lightText}>
                                • New businesses will automatically get a GDMS site created
                            </Text>
                            <Text fontSize="sm" color={theme.colors.lightText}>
                                • Hardware phones can be managed through GDMS
                            </Text>
                            <Text fontSize="sm" color={theme.colors.lightText}>
                                • OAuth tokens are automatically refreshed
                            </Text>
                        </VStack>
                    </Box>
                </Box>
            ) : (
                <Center height="100vh" width="100%">
                    <VStack spacing={6} maxW="500px" p={6}>
                        <Text fontSize="xl" fontWeight="bold">GDMS Integration</Text>
                        <Text textAlign="center" color={theme.colors.lightText}>
                            Connect to Grandstream Device Management System (GDMS) to automatically
                            create sites for new businesses and manage hardware phones.
                        </Text>

                        {status?.lastError?.message && (
                            <Alert status="error" borderRadius="md">
                                <AlertIcon />
                                <Box flex="1">
                                    <AlertTitle>Connection Error</AlertTitle>
                                    <AlertDescription fontSize="sm">
                                        {status.lastError.message}
                                    </AlertDescription>
                                </Box>
                            </Alert>
                        )}

                        <Button
                            colorScheme="blue"
                            onClick={onOpen}
                            size="lg"
                        >
                            Connect to GDMS
                        </Button>

                        {/* Credential Input Modal */}
                        <Modal isOpen={isOpen} onClose={onClose} size="lg">
                            <ModalOverlay />
                            <ModalContent>
                                <ModalHeader>Connect to GDMS</ModalHeader>
                                <ModalCloseButton />
                                <ModalBody>
                                    <VStack spacing={4}>
                                        <FormControl isRequired>
                                            <FormLabel>Username</FormLabel>
                                            <Input
                                                type="text"
                                                placeholder="GDMS Username"
                                                value={credentials.username}
                                                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                                            />
                                        </FormControl>

                                        <FormControl isRequired>
                                            <FormLabel>Password</FormLabel>
                                            <Input
                                                type="password"
                                                placeholder="GDMS Password"
                                                value={credentials.password}
                                                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                                            />
                                        </FormControl>

                                        <FormControl isRequired>
                                            <FormLabel>Client ID</FormLabel>
                                            <Input
                                                type="text"
                                                placeholder="Client ID"
                                                value={credentials.clientId}
                                                onChange={(e) => setCredentials({ ...credentials, clientId: e.target.value })}
                                            />
                                        </FormControl>

                                        <FormControl isRequired>
                                            <FormLabel>Client Secret</FormLabel>
                                            <Input
                                                type="password"
                                                placeholder="Client Secret"
                                                value={credentials.clientSecret}
                                                onChange={(e) => setCredentials({ ...credentials, clientSecret: e.target.value })}
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel>Region</FormLabel>
                                            <Select
                                                value={credentials.region}
                                                onChange={(e) => setCredentials({ ...credentials, region: e.target.value })}
                                            >
                                                <option value="us">United States</option>
                                                <option value="eu">Europe</option>
                                            </Select>
                                        </FormControl>
                                    </VStack>
                                </ModalBody>
                                <ModalFooter>
                                    <Button variant="ghost" mr={3} onClick={onClose}>
                                        Cancel
                                    </Button>
                                    <Button
                                        colorScheme="blue"
                                        onClick={handleConnect}
                                        isLoading={isConnecting}
                                        isDisabled={!credentials.username || !credentials.password || !credentials.clientId || !credentials.clientSecret}
                                    >
                                        Connect
                                    </Button>
                                </ModalFooter>
                            </ModalContent>
                        </Modal>

                        <Box
                            borderWidth="1px"
                            borderRadius="md"
                            p={4}
                            bg={theme.colors.gray[50]}
                            width="100%"
                        >
                            <Text fontSize="sm" fontWeight="semibold" mb={2}>Requirements:</Text>
                            <VStack align="stretch" spacing={1} fontSize="sm" color={theme.colors.lightText}>
                                <Text>• GDMS account with API Developer access</Text>
                                <Text>• Client ID and Client Secret from GDMS</Text>
                                <Text>• Valid GDMS username and password</Text>
                            </VStack>
                        </Box>
                    </VStack>
                </Center>
            )}
        </>
    );
}

export default GDMSSettings;
